import { useEffect, useRef, useState, type CSSProperties, type PointerEvent } from "react";
import {
  CHAT_SIDEBAR_DEFAULT_WIDTH,
  CHAT_SIDEBAR_MAX_WIDTH,
  CHAT_SIDEBAR_MIN_WIDTH,
  clampSidebarWidth,
} from "./Chat.helpers.ts";
import { useAppActions } from "../app/AppProvider.tsx";
import { ChatList } from "./ChatList.tsx";
import { ChatRoom } from "./ChatRoom.tsx";

const STORAGE_KEY = "wcrm.chatSidebarWidth";

type ChatLayoutStyle = CSSProperties & {
  "--chat-sidebar-width": string;
};

export function ChatLayout() {
  const { state } = useAppActions();
  const [width, setWidth] = useState(readInitialWidth);
  const widthRef = useRef(width);
  widthRef.current = width;

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, String(width));
  }, [width]);

  function beginResize(event: PointerEvent<HTMLDivElement>) {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = widthRef.current;
    document.body.classList.add("is-resizing-chat-sidebar");

    const onMove = (moveEvent: globalThis.PointerEvent) => {
      setWidth(clampSidebarWidth(startWidth + moveEvent.clientX - startX));
    };
    const onEnd = () => {
      document.body.classList.remove("is-resizing-chat-sidebar");
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onEnd);
      window.removeEventListener("pointercancel", onEnd);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onEnd);
    window.addEventListener("pointercancel", onEnd);
  }

  function resizeBy(delta: number) {
    setWidth((current) => clampSidebarWidth(current + delta));
  }

  return (
    <main
      className={`main main-chats ${state.activeChatJid ? "has-active-chat" : ""}`}
      style={{ "--chat-sidebar-width": `${width}px` } as ChatLayoutStyle}
    >
      <ChatList />
      <div
        className="chat-resize-handle"
        role="separator"
        aria-label="Redimensionar lista de conversas"
        aria-orientation="vertical"
        aria-valuemin={CHAT_SIDEBAR_MIN_WIDTH}
        aria-valuemax={CHAT_SIDEBAR_MAX_WIDTH}
        aria-valuenow={width}
        tabIndex={0}
        onPointerDown={beginResize}
        onDoubleClick={() => setWidth(CHAT_SIDEBAR_DEFAULT_WIDTH)}
        onKeyDown={(event) => {
          if (event.key === "ArrowLeft") resizeBy(event.shiftKey ? -40 : -16);
          if (event.key === "ArrowRight") resizeBy(event.shiftKey ? 40 : 16);
          if (event.key === "Home") setWidth(CHAT_SIDEBAR_MIN_WIDTH);
          if (event.key === "End") setWidth(CHAT_SIDEBAR_MAX_WIDTH);
        }}
      />
      <ChatRoom />
    </main>
  );
}

function readInitialWidth(): number {
  if (typeof window === "undefined") return CHAT_SIDEBAR_DEFAULT_WIDTH;
  return clampSidebarWidth(Number(window.localStorage.getItem(STORAGE_KEY) || CHAT_SIDEBAR_DEFAULT_WIDTH));
}
